import { styled } from "@linaria/react";
import { Button } from "primereact/button";
import { styleTokens } from "@ideally-ltd/design-tokens";
import { boxShadowToString } from "helpers/cssColors";

const PButton = styled(Button)<{ ref: React.ForwardedRef<Button> }>`
  // common styles
  &.p-button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: min-content;
    width: min-content;
    padding: 0 ${styleTokens.button.padding.x};
    min-height: 32px;
    gap: ${styleTokens.button.gap};
    border-width: 1px;

    &.p-button > .p-button-label {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: fit-content;
    }
  }

  // common child styles
  & > .p-button-label,
  & > .material-symbols-sharp,
  & > .p-button-icon {
    font-size: ${styleTokens.fontSize.sm};
    font-weight: ${styleTokens.button.label.font.weight};
    margin: 0px !important;
  }

  // icon
  &.p-button-icon-right {
    flex-direction: row-reverse;
    order: inherit;
  }
  &.p-button-icon-only {
    gap: 0px;
    padding: ${styleTokens.button.padding.y} ${styleTokens.button.padding.y};

    & i {
      margin-left: auto !important;
      margin-right: auto !important;
    }

    &.p-button-round {
      width: ${styleTokens.button.icon.only.width};
      height: ${styleTokens.button.icon.only.width};
    }
  }

  // round
  &.p-button.p-component:not(.p-button-round) {
    border-radius: ${styleTokens.button.border.radius};
  }
  &.p-button.p-component.p-button-round {
    border-radius: ${styleTokens.button.rounded.border.radius};
  }

  // raised
  &.p-button.p-component:not(.p-button-raised):not(.p-button-filled) {
    box-shadow: none;
  }
  &.p-button.p-component.p-button-raised {
    box-shadow: ${boxShadowToString(styleTokens.button.raised.shadow)};
  }

  // disabled state
  &.p-button.p-component.p-button-disabled {
    opacity: 0.5;
    cursor: default;
  }

  &.p-button.p-button-secondary:enabled:focus {
    box-shadow: ${boxShadowToString(styleTokens.button.raised.shadow)};
  }

  // loading state
  &.p-button.p-button-loading > .material-symbols-sharp {
    animation: spin 1.5s linear infinite;
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  }

  // filled
  &.p-button.p-button-filled {
    box-shadow: ${boxShadowToString(styleTokens.button.shadow)};
    &.p-button.p-button-primary {
      background-color: ${styleTokens.button.primary.background};
      color: ${styleTokens.button.primary.color};
      border-color: ${styleTokens.button.primary.border.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.primary.hover.background};
        color: ${styleTokens.button.primary.hover.color};
        border-color: ${styleTokens.button.primary.hover.border.color};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.primary.active.background};
        color: ${styleTokens.button.primary.active.color};
        border-color: ${styleTokens.button.primary.active.border.color};
      }
    }
    &.p-button.p-button-secondary {
      background-color: ${styleTokens.button.secondary.background};
      color: ${styleTokens.button.secondary.color};
      border-color: ${styleTokens.button.secondary.border.color};

      &.p-button-hovered {
        background-color: ${styleTokens.button.secondary.hover.background};
        color: ${styleTokens.button.secondary.hover.color};
        border-color: ${styleTokens.button.secondary.hover.border.color};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.secondary.active.background};
        color: ${styleTokens.button.secondary.active.color};
        border-color: ${styleTokens.button.secondary.active.border.color};
      }
    }
    &.p-button.p-button-danger {
      background-color: ${styleTokens.button.danger.background};
      color: ${styleTokens.button.danger.color};
      border-color: ${styleTokens.button.danger.border.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.danger.hover.background};
        color: ${styleTokens.button.danger.hover.color};
        border-color: ${styleTokens.button.danger.hover.border.color};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.danger.active.background};
        color: ${styleTokens.button.danger.active.color};
        border-color: ${styleTokens.button.danger.active.border.color};
      }
    }
  }

  // text
  &.p-button.p-button-text {
    background-color: transparent;
    border-color: transparent;
    &.p-button.p-button-primary {
      color: ${styleTokens.button.text.primary.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.text.primary.hover.background};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.text.primary.active.background};
      }
    }
    &.p-button.p-button-secondary {
      color: ${styleTokens.button.text.secondary.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.text.secondary.hover.background};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.text.secondary.active.background};
      }
    }
    &.p-button.p-button-danger {
      color: ${styleTokens.button.text.danger.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.text.danger.hover.background};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.text.danger.active.background};
      }
    }
  }

  // outlined
  &.p-button.p-button-outlined {
    background-color: transparent;
    &.p-button.p-button-primary {
      color: ${styleTokens.button.outlined.primary.color};
      border-color: ${styleTokens.button.outlined.primary.border.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.outlined.primary.hover.background};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.outlined.primary.active.background};
      }
    }
    &.p-button.p-button-secondary {
      color: ${styleTokens.button.outlined.secondary.color};
      border-color: ${styleTokens.button.outlined.secondary.border.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.outlined.secondary.hover.background};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.outlined.secondary.active.background};
      }
    }
    &.p-button.p-button-danger {
      color: ${styleTokens.button.outlined.danger.color};
      border-color: ${styleTokens.button.outlined.danger.border.color};
      &.p-button-hovered {
        background-color: ${styleTokens.button.outlined.danger.hover.background};
      }
      &.p-button-active {
        background-color: ${styleTokens.button.outlined.danger.active.background};
      }
    }
  }
`;
export const PButtonStyle = {
  PButton,
};
